import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Avatar,
  Flex,
  Tooltip,
  Modal,
  Input,
  List,
  Tag,
  Button,
  Typography,
  Space,
  theme
} from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./EmpShifts.css";
import { useTranslation } from "react-i18next";
import { Error, Success } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import { SocketContext } from "../../../context/SocketContext";
import {
  expandShift,
  getVisibleRange,
  DATE_KEY,
  DEFAULT_SHIFT_COLOR
} from "../../../utils/shiftUtils";
import { shiftApi } from "../../../serviceProvider/components/shiftsPlanning/shiftApi";
import { calendarMessages } from "../../../serviceProvider/components/shiftsPlanning/calendarMessages";
import { useIsMobile } from "../../../design-system/useMediaQuery";

const localizer = momentLocalizer(moment);
const { Text } = Typography;
const STATUS_COLOR = { Pending: "gold", Approved: "green", Rejected: "red" };
// Days listed by the agenda (the phone default); the same range is expanded below
const AGENDA_DAYS = 30;

/**
 * Calendar of one employee's shifts. With `selfService` (the employee viewing their own
 * shifts) clicking an upcoming shift asks the planner to swap it, and their requests are listed.
 */
export const EmpShifts = ({ employeeObject, selfService = false }) => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const socket = useContext(SocketContext);
  const [shifts, setShifts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [calendarView, setCalendarView] = useState(isMobile ? Views.AGENDA : Views.MONTH);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [swapTarget, setSwapTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const employeeId = employeeObject?._id;

  // Only the days this employee actually works (single-day changes can add or remove them)
  const calendarEvents = useMemo(() => {
    const { start, end } =
      calendarView === Views.AGENDA
        ? {
            start: moment(calendarDate).startOf("day").toDate(),
            end: moment(calendarDate).add(AGENDA_DAYS, "days").endOf("day").toDate()
          }
        : getVisibleRange(calendarDate, calendarView);
    return shifts
      .flatMap(shift => expandShift(shift, start, end))
      .filter(
        occ => !occ.cancelled && (occ.employees || []).some(e => (e?._id || e) === employeeId)
      );
  }, [shifts, calendarDate, calendarView, employeeId]);

  const pendingKeys = useMemo(
    () =>
      new Set(requests.filter(r => r.status === "Pending").map(r => `${r.shift?._id}_${r.date}`)),
    [requests]
  );

  const getAllShifts = async () => {
    if (!employeeId) return;
    try {
      const res = await shiftApi.get(`/employee/${employeeId}`);
      if (res.status === 200 && Array.isArray(res.data)) {
        setShifts(res.data);
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      Error(err.message);
    }
  };

  const getRequests = async () => {
    if (!selfService) return;
    try {
      const res = await shiftApi.get("/swap-requests");
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const refreshData = () => {
    getAllShifts();
    getRequests();
  };

  useEffect(() => {
    refreshData();

    if (socket) {
      socket.on("refresh service provider data", refreshData);
    }

    return () => {
      if (socket) {
        socket.off("refresh service provider data", refreshData);
      }
    };
  }, [socket, employeeId]);

  const sendSwapRequest = async () => {
    if (!swapTarget || sending) return;
    setSending(true);
    try {
      const res = await shiftApi.send("post", "/swap-requests", {
        shift: swapTarget._originalId,
        date: swapTarget._dateKey,
        reason
      });
      Success(res.data.successMessage);
      setSwapTarget(null);
      setReason("");
      getRequests();
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setSending(false);
    }
  };

  const withdraw = async id => {
    try {
      const res = await shiftApi.send("delete", `/swap-requests/${id}`);
      Success(res.data.successMessage);
      getRequests();
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const eventPropGetter = event => {
    const colorCode = event.colorCode;
    return {
      className: "calendarEvent",
      style: {
        backgroundColor: colorCode || DEFAULT_SHIFT_COLOR,
        borderColor: colorCode || DEFAULT_SHIFT_COLOR,
        color: token.colorTextLightSolid
      }
    };
  };

  const EventComponent = ({ event }) => {
    const swapPending = pendingKeys.has(`${event._originalId}_${event._dateKey}`);
    return (
      <div className="EventComponent">
        <div className="my-1">
          {swapPending && (
            <Tooltip title={t("Swap requested")}>
              <SwapOutlined style={{ marginInlineEnd: 4 }} />
            </Tooltip>
          )}
          {event.title} · {moment(event.start).format("HH:mm")}–{moment(event.end).format("HH:mm")}
        </div>
        {calendarView === "day" ? (
          <Flex gap={6} align="center" wrap="wrap">
            {event?.employees?.map(employee => (
              <Tooltip
                title={
                  <div>
                    {employee?.firstName} {employee?.lastName}
                  </div>
                }
                key={employee?._id}
              >
                <Avatar src={employee?.picture?.url || null} size="large">
                  {employee?.firstName?.substring(0, 2).toUpperCase()}
                </Avatar>
              </Tooltip>
            ))}
          </Flex>
        ) : (
          calendarView === "week" && (
            <Avatar.Group
              size="small"
              className="mt-2"
              max={{
                count: 4,
                style: {
                  color: token.colorPrimary,
                  backgroundColor: token.colorPrimaryBg,
                  cursor: "pointer"
                },
                popover: { trigger: "hover" }
              }}
            >
              {event?.employees?.map(employee => (
                <Avatar key={employee?._id} src={employee?.picture?.url || null}>
                  {employee?.firstName?.substring(0, 2).toUpperCase()}
                </Avatar>
              ))}
            </Avatar.Group>
          )
        )}
        {/* The agenda (phone default) has no clickable events, so offer the swap as a button */}
        {selfService &&
          calendarView === Views.AGENDA &&
          !swapPending &&
          event.start >= new Date() && (
            <Button
              className="emp-shifts__swap"
              icon={<SwapOutlined />}
              onClick={() => onSelectEvent(event)}
            >
              {t("Request a shift swap")}
            </Button>
          )}
      </div>
    );
  };

  const onSelectEvent = event => {
    if (!selfService) return;
    if (event.start < new Date()) {
      Error(t("Past shifts cannot be swapped"));
      return;
    }
    if (pendingKeys.has(`${event._originalId}_${event._dateKey}`)) {
      Error(t("You already requested a swap for this day"));
      return;
    }
    setSwapTarget(event);
  };

  return (
    <div className="EmpShifts emp-shifts">
      <div className="content container-fluid">
        {selfService && (
          <Text type="secondary">{t("Click an upcoming shift to ask for a swap.")}</Text>
        )}
        <Calendar
          className={`emp-shifts__calendar is-${calendarView}`}
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          view={calendarView}
          onView={e => setCalendarView(e)}
          date={calendarDate}
          onNavigate={d => setCalendarDate(d)}
          style={{ height: isMobile ? "calc(100dvh - 200px)" : "75vh", minHeight: 460 }}
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          length={AGENDA_DAYS}
          rtl={i18n.dir() === "rtl"}
          messages={calendarMessages(t, {
            event: t("Shifts"),
            noEventsInRange: t("No shifts in this period")
          })}
          eventPropGetter={eventPropGetter}
          onSelectEvent={onSelectEvent}
          components={{
            event: EventComponent
          }}
        />

        {selfService && requests.length > 0 && (
          <List
            header={<Text strong>{t("My swap requests")}</Text>}
            dataSource={requests}
            renderItem={request => (
              <List.Item
                key={request._id}
                actions={
                  request.status === "Pending"
                    ? [
                        <Button key="withdraw" size="small" onClick={() => withdraw(request._id)}>
                          {t("Withdraw")}
                        </Button>
                      ]
                    : []
                }
              >
                <Space direction="vertical" size={2}>
                  <Space wrap>
                    <Tag color={request.shift?.color}>{request.shift?.title}</Tag>
                    {moment(request.date, DATE_KEY).format("ddd DD/MMM/YYYY")}
                    <Tag color={STATUS_COLOR[request.status]}>{t(request.status)}</Tag>
                  </Space>
                  {request.responseNote && <Text type="secondary">{request.responseNote}</Text>}
                </Space>
              </List.Item>
            )}
          />
        )}
      </div>

      <Modal
        title={t("Request a shift swap")}
        open={!!swapTarget}
        onOk={sendSwapRequest}
        onCancel={() => setSwapTarget(null)}
        okText={t("Send request")}
        confirmLoading={sending}
        destroyOnClose
      >
        {swapTarget && (
          <>
            <p>
              <Tag color={swapTarget.colorCode}>{swapTarget.title}</Tag>
              {moment(swapTarget.start).format("ddd DD/MMM/YYYY HH:mm")} –{" "}
              {moment(swapTarget.end).format("HH:mm")}
            </p>
            <Input.TextArea
              rows={3}
              maxLength={500}
              placeholder={t("Reason (optional)")}
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              {t("Your planner decides who takes over the shift.")}
            </Text>
          </>
        )}
      </Modal>
    </div>
  );
};
